import { useState } from "react";
import { Send, CheckCircle2, MessageCircle } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { api, formatApiError } from "@/lib/api";
import { useSite, useBrand } from "@/context/SiteContext";
import { useSeo } from "@/lib/seo";
import { REQUIREMENT_OPTIONS, CUSTOMER_TYPES } from "@/data/content";

export default function GetQuote() {
  const { waLink } = useSite();
  const brand = useBrand();
  const [form, setForm] = useState({});
  const [state, setState] = useState({ loading: false, done: false, err: "" });
  useSeo({ title: `Get a Quote | ${brand.name}`, description: "Request a quote for solar inverters, hybrid systems, batteries, UPS, ESS, EV chargers, solar modules, BOS or complete solar EPC projects.", canonical: `https://${brand.domain}/get-a-quote` });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) { setState({ ...state, err: "Name and Mobile are required." }); return; }
    setState({ loading: true, done: false, err: "" });
    try {
      await api.post("/leads", { ...form, source: "Get a Quote Page" });
      setState({ loading: false, done: true, err: "" });
    } catch (e2) {
      setState({ loading: false, done: false, err: formatApiError(e2.response?.data?.detail) });
    }
  };

  return (
    <div>
      <PageHeader label="Get a Quote" title="Request Your Energy Solution Quote" sub="Tell us your requirement, load and application. Our team will help you select the right combination of solar, inverter, battery and power backup solutions." />
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="card-surface p-6 sm:p-8">
          {state.done ? (
            <div className="text-center py-10" data-testid="getquote-success">
              <CheckCircle2 className="mx-auto text-emerald-400 mb-4" size={56} />
              <h3 className="font-display text-2xl font-bold text-white">Requirement Received</h3>
              <p className="text-slate-400 mt-2 mb-6">Thank you. Our team will get in touch with you shortly.</p>
              <a href={waLink()} target="_blank" rel="noreferrer" className="btn-primary" data-testid="getquote-whatsapp"><MessageCircle size={16} /> Continue on WhatsApp</a>
            </div>
          ) : (
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
              <input data-testid="getquote-name" placeholder="Name *" onChange={set("name")} className="cf" />
              <input data-testid="getquote-mobile" placeholder="Mobile *" onChange={set("mobile")} className="cf" />
              <input data-testid="getquote-email" placeholder="Email" onChange={set("email")} className="cf" />
              <input data-testid="getquote-company" placeholder="Company" onChange={set("company")} className="cf" />
              <input data-testid="getquote-city" placeholder="City" onChange={set("city")} className="cf" />
              <select data-testid="getquote-customer-type" onChange={set("customer_type")} className="cf" defaultValue="">
                <option value="" disabled>Customer Type</option>
                {CUSTOMER_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select data-testid="getquote-requirement" onChange={set("requirement")} className="cf" defaultValue="">
                <option value="" disabled>Product / Service</option>
                {REQUIREMENT_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <input data-testid="getquote-capacity" placeholder="Approximate Capacity" onChange={set("capacity")} className="cf" />
              <textarea data-testid="getquote-message" placeholder="Requirement Details" rows={4} onChange={set("message")} className="cf sm:col-span-2 resize-none" />
              {state.err && <div className="text-red-400 text-sm sm:col-span-2" data-testid="getquote-error">{state.err}</div>}
              <button type="submit" disabled={state.loading} className="btn-primary sm:col-span-2" data-testid="getquote-submit"><Send size={16} /> {state.loading ? "Submitting..." : "Submit Requirement"}</button>
            </form>
          )}
        </div>
      </section>
      <style>{`.cf{width:100%;background:#060B11;border:1px solid rgba(255,255,255,.1);border-radius:.6rem;padding:.8rem 1rem;color:#E2E8F0;font-size:.9rem;outline:none}.cf:focus{border-color:#10B981}`}</style>
    </div>
  );
}
